import { useEffect, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { RefreshCw } from "lucide-react";
import { api } from "@/lib/api";
import { Switch } from "@/components/ui/switch";
import { PcoReview } from "@/components/PcoReview";
import { PcoFieldCheck } from "@/components/PcoFieldCheck";
import { PCO_ERRORS } from "@/components/PcoConnect";

const STATUS = { created: "Added as new person", matched: "Updated existing person", review: "Needs review (possible duplicate)", pending: "Waiting", error: "Failed" };

const Toggle = ({ label, hint, checked, onChange, testid, disabled }) => (
  <div className="flex items-center justify-between gap-4 py-3">
    <div><p className="text-sm text-navy">{label}</p>{hint && <p className="text-xs text-muted-ink">{hint}</p>}</div>
    <Switch checked={checked} disabled={disabled} onCheckedChange={onChange} data-testid={testid} />
  </div>
);

const SyncLog = ({ log, onRetry, busy }) => (
  <div className="mt-6" data-testid="pco-sync-log">
    <p className="text-xs uppercase tracking-[0.12em] text-muted-ink">Recent syncs</p>
    {log.length === 0 ? <p className="text-sm text-muted-ink mt-3" data-testid="pco-sync-log-empty">No syncs yet. New results appear here once volunteers agree to be added.</p> : (
      <ul className="divide-y divide-[var(--line)] mt-2">
        {log.map((l) => (
          <li key={l.at + l.volunteer_id} className="py-3 flex flex-wrap items-center justify-between gap-3 text-sm" data-testid={`pco-log-${l.volunteer_id}`}>
            <div>
              <p className="text-navy font-medium">{l.volunteer_id ? <Link to={`/volunteers/${l.volunteer_id}`} className="underline decoration-dotted hover:text-teal" data-testid={`pco-log-link-${l.volunteer_id}`}>{l.name}</Link> : l.name} <span className="text-muted-ink font-normal">· {l.email}</span></p>
              <p className={l.status === "error" ? "text-red-700" : "text-muted-ink"}>{STATUS[l.status]}{l.error ? ` · ${l.error}` : ""} · {new Date(l.at).toLocaleString()}</p>
            </div>
            {l.status === "error" && l.volunteer_id && <button disabled={busy} onClick={() => onRetry(l.volunteer_id)} className="chip-btn flex items-center gap-1.5" data-testid={`pco-retry-${l.volunteer_id}`}><RefreshCw size={13} /> Retry</button>}
          </li>
        ))}
      </ul>
    )}
  </div>
);

export const PlanningCenter = ({ church }) => {
  const [s, setS] = useState(null);
  const [busy, setBusy] = useState(false);
  const [params, setParams] = useSearchParams();
  const load = () => api.get("/integrations/pco").then((r) => setS(r.data));
  useEffect(() => { load(); }, []);
  useEffect(() => {
    const r = params.get("pco");
    if (!r) return;
    if (r === "connected") toast.success("Connected to Planning Center", { id: "pco-connected" });
    else toast.error(PCO_ERRORS[r] || "Could not connect Planning Center", { duration: 10000 });
    setParams({}, { replace: true });
  }, [params, setParams]);
  if (!s) return null;
  const owner = church.role === "owner";

  const save = async (patch) => {
    setBusy(true);
    try {
      const { data } = await api.put("/integrations/pco", patch);
      setS((p) => ({ ...p, ...data }));
      toast.success("Planning Center saved");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Could not save");
    } finally {
      setBusy(false);
    }
  };
  const connect = async () => {
    setBusy(true);
    try {
      const { data } = await api.post("/integrations/pco/start", { origin_url: window.location.origin });
      window.location.href = data.url;
    } catch (err) {
      toast.error(err.response?.data?.detail || "Could not start Planning Center sign-in");
      setBusy(false);
    }
  };
  const retry = async (vid) => {
    setBusy(true);
    try {
      const { data } = await api.post(`/integrations/pco/sync/${vid}`);
      ["error", "review", "pending"].includes(data?.pco_status) ? toast.error(data.pco_error || "Sync failed again") : toast.success("Synced to Planning Center");
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Could not sync");
    } finally {
      setBusy(false);
    }
  };
  const disconnect = async () => { await api.delete("/integrations/pco"); load(); toast.success("Disconnected"); };

  return (
    <section className="card-soft p-8 md:p-10 mt-6" data-testid="pco-section">
      <div className="flex items-center justify-between gap-4">
        <div>
          <p className="font-display text-navy text-lg font-semibold">Planning Center sync</p>
          <p className="text-sm text-muted-ink mt-1">With each volunteer's consent, their name, email, phone and team interests are added to Planning Center People. Existing people are matched by email, so no duplicates.</p>
        </div>
        <span className={`text-xs rounded-full px-3 py-1 whitespace-nowrap ${s.connected ? "bg-teal text-white" : "bg-paper text-muted-ink"}`} data-testid="pco-status">{s.connected ? `Connected${s.org ? ` · ${s.org}` : ""}` : "Not connected"}</span>
      </div>
      {!owner && <p className="text-sm text-muted-ink mt-6" data-testid="pco-owner-only">Only the church owner can change Planning Center settings.</p>}
      {owner && (s.connected ? (
        <>
          {s.reauth_required && <p className="text-sm text-amber-800 bg-amber-50 rounded-xl p-4 mt-6" data-testid="pco-settings-reauth-warning">Planning Center sign-in has expired. Click Disconnect below, then Connect again. Your volunteers and results stay safe.</p>}
          <div className="bg-paper rounded-xl px-5 py-2 mt-6 divide-y divide-[var(--line)]">
            <Toggle label="Sync new results automatically" hint="Paused until you approve activation. You can sync volunteers one at a time." checked={s.enabled} disabled={busy} onChange={(v) => save({ enabled: v })} testid="pco-enabled-switch" />
            <Toggle label="Include best-fit team interests" hint="Saved to your Dream Teams tab in Planning Center" checked={s.sync_teams} disabled={busy} onChange={(v) => save({ sync_teams: v })} testid="pco-sync-teams-switch" />
            <Toggle label="Include DISC personality style" hint="Off by default. Kept private in Dream Teams." checked={s.sync_disc} disabled={busy} onChange={(v) => save({ sync_disc: v })} testid="pco-sync-disc-switch" />
            <Toggle label="Include top spiritual gifts" hint="Off by default. Kept private in Dream Teams." checked={s.sync_gifts} disabled={busy} onChange={(v) => save({ sync_gifts: v })} testid="pco-sync-gifts-switch" />
          </div>
          <PcoFieldCheck />
          <PcoReview onChange={load} />
          <SyncLog log={s.log} onRetry={retry} busy={busy} />
          <button onClick={disconnect} className="text-sm text-red-700 mt-6" data-testid="pco-disconnect-btn">Disconnect Planning Center</button>
        </>
      ) : (
        <div className="mt-6">
          <button onClick={connect} disabled={busy} className="btn-navy !py-2.5" data-testid="pco-connect-btn">{busy ? "Opening Planning Center..." : "Connect Planning Center"}</button>
          <p className="text-xs text-muted-ink mt-3">You'll sign in to Planning Center and click Allow. We only ask for access to People.</p>
        </div>
      ))}
    </section>
  );
};
